import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Link, styled, TextField, Typography, useTheme } from "@mui/material";
import { CustomCircularProgress } from "components/shared/layout";
import { useAbstractWorkItemContext, useWorkItemPermissions, useWorkItemProductMaster } from "hooks/work-items";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from 'react-router-dom';
import { ArrayLabel, Ico_NewWindow, Ico_Search, Ico_Warning, useSnackbar } from '@sagecentral/core-ui';
import { KeyGrid, KeyValueRow, ValueGrid } from "components/shared";
import { IProductMaster } from "models/product-master";
import WorkItemProductMasterGrid from "./grid/WorkItemProductMasterGrid";
import { ILinkInformation } from "models/common";


const FormTitle = styled(Box)({
    fontSize: '22px',
    marginBottom: '24px',
    fontWeight: 'bold'
});

type WorkItemProductMasterProps = {
    draft?: boolean;
};

const WorkItemProductMaster = (props: WorkItemProductMasterProps) => {
    const workItemContext = useAbstractWorkItemContext();
    const permissions = useWorkItemPermissions();
    const { workItem, template } = workItemContext;
    const { draft } = props;
    const theme = useTheme();
    const [bookIdText, setBookIdText] = useState<string>('');
    const [bookInformation, setBookInformation] = useState<IProductMaster | null>(null);
    const [openUnlinkDialog, setOpenUnlinkDialog] = useState(false);
    const [openLinkDialog, setOpenLinkDialog] = useState(false);
    const { displaySnackbar } = useSnackbar();

    const [
        productMaster,
        isLinked,
        searchBookId,
        refreshBookId,
        unlinkBookId,
        linkBookId,
        refresh,
        loading
    ] = useWorkItemProductMaster(workItem?.id ?? 0, draft ?? false);

    useEffect(() => {
        if (productMaster) {
            setBookInformation(productMaster);
        }
    }, [productMaster])


    if (loading) {
        return <CustomCircularProgress />
    }

    const handleRefreshBookId = async (workItemId: number, bookIdText: number) => {
        if (bookIdText === 0) {
            displaySnackbar('warning', 'Book ID is required.');
            return;
        }
        var isRefreshDone = await refreshBookId(workItemId, bookIdText)
        if (isRefreshDone) {
            var bookInfo = await searchBookId(workItemId, bookIdText);

            if(bookInfo != null && bookInfo != undefined && bookInformation?.SourceLink)
            {
                bookInfo.SourceLink = bookInformation.SourceLink;
            }

            setBookInformation(bookInfo);
        }
    }

    const handleSeachBookId = async () => {
        if (bookIdText == '') {
            displaySnackbar('warning', 'Book ID is required.');
            return;
        }
        var bookInfo = await searchBookId(workItem?.id ?? 0, parseInt(bookIdText))
        setBookInformation(bookInfo);
    }

    const handleLink = async (workitemId: number, bookId: number, title: string) => {
        await linkBookId(workitemId, bookId, title);
        setOpenLinkDialog(false);
        refresh();
    }

    const handleUnlink = async (workitemId: number, bookId: number) => {
        await unlinkBookId(workitemId, bookId);
        setOpenUnlinkDialog(false);
        setBookInformation(null);
        setBookIdText('');
    }

    const clearSearchResults = () => {
        setBookInformation(null);
        setBookIdText('');
    }

    if (template?.productMasterSourceId === 1 || template?.productMasterSourceId === 2) {
        return (
            <Box >
                {
                    draft &&
                    <FormTitle>
                        Product Master
                    </FormTitle>
                }
                {!isLinked &&
                    <>
                        <KeyGrid sx={{ height: '2.5em', alignContent: 'center' }}>{template?.productMasterSourceId === 1 ? '(SMART Books) Book ID:' : '(Biblio) Work Ref:'}</KeyGrid>
                        <ValueGrid>
                            <TextField
                                type="number"
                                value={bookIdText}
                                onChange={(v) => setBookIdText(v.target.value)}
                            />
                            <IconButton sx={{ pt: '3px' }} onClick={() => handleSeachBookId()}>
                                <Ico_Search sx={{ width: '32px', height: '32px' }} />
                            </IconButton>
                        </ValueGrid>
                    </>
                }

                {bookInformation &&
                    <>
                        <Grid container>
                            {!isLinked &&
                                <Divider sx={{ width: '100%', mb: '32px', mt: '24px' }} />
                            }
                            <KeyValueRow>
                                <KeyGrid>Book ID:</KeyGrid>
                                <ValueGrid>
                                    {bookInformation.BookId}
                                </ValueGrid>
                                {isLinked &&
                                    <Link sx={{ ml: '1em' }} onClick={() => handleRefreshBookId(workItem?.id ?? 0, bookInformation.BookId)}>
                                        Refresh
                                    </Link>
                                }
                                {permissions?.canUnlinkProductMaster && isLinked &&
                                    <Link sx={{ ml: '1em' }} onClick={() => setOpenUnlinkDialog(true)}>
                                        Unlink
                                    </Link>
                                }
                            </KeyValueRow>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                mb: 1
                            }}>
                                <KeyGrid
                                    sx={{
                                        flexShrink: 0,
                                        minWidth: '45px',
                                    }}>Title:</KeyGrid>
                                <ValueGrid
                                    sx={{
                                        flexGrow: 1,
                                        maxWidth: '80%',
                                        ml: 1
                                    }}
                                >
                                    {bookInformation.Title}
                                </ValueGrid>
                            </Box>
                            {bookInformation.SourceLink &&     
                                <KeyValueRow sx={{ height: '3em' }}>
                                    <ValueGrid sx={{ ml: '0px', height: '3em' }}>
                                        <Link target='_blank' to={bookInformation.SourceLink?.url ?? ''} component={RouterLink}>
                                            {bookInformation.SourceLink?.text}
                                        </Link>
                                    </ValueGrid>
                                </KeyValueRow>             
                            }
                            <KeyValueRow>
                                <KeyGrid>Publisher:</KeyGrid>
                                <ValueGrid>
                                    {bookInformation.Publisher}
                                </ValueGrid>
                            </KeyValueRow>
                            <KeyValueRow>
                                <KeyGrid>Subtitle:</KeyGrid>
                                <ValueGrid>
                                    {bookInformation.Subtitle}
                                </ValueGrid>
                            </KeyValueRow>
                            <KeyValueRow>
                                <KeyGrid>Edition:</KeyGrid>
                                <ValueGrid>
                                    {bookInformation.Edition}
                                </ValueGrid>
                            </KeyValueRow>
                            <KeyValueRow>
                                <KeyGrid>Contributor(s):</KeyGrid>
                                <ValueGrid>
                                    <ArrayLabel items={bookInformation.contributors?.map((c) => c.name) || []} />
                                </ValueGrid>
                            </KeyValueRow>
                            <Divider sx={{ width: '100%', mb: '32px' }} />
                            <KeyValueRow>
                                <KeyGrid>Product Information</KeyGrid>
                            </KeyValueRow>

                            <WorkItemProductMasterGrid data={bookInformation.products ?? []} />
                            <Dialog open={openLinkDialog} fullWidth={false} maxWidth={'lg'} onClose={() => setOpenLinkDialog(false)}>
                                <DialogTitle>
                                    <Ico_Warning color='error' sx={{ height: '40px', width: '40px' }} />
                                    <br />
                                    {<>Link the Product Master</>}
                                </DialogTitle>
                                <DialogContent>
                                    {
                                        <>
                                            Are you sure you want to link the product master?
                                        </>
                                    }
                                    <DialogActions sx={{ mt: '24px' }}>
                                        <Button fullWidth={true} variant='contained' onClick={() => handleLink(workItem?.id ?? 0, bookInformation.BookId, bookInformation.Title)}>Link</Button>
                                        <Button fullWidth variant="outlined" onClick={() => { setOpenLinkDialog(false) }}>
                                            Cancel
                                        </Button>
                                    </DialogActions>
                                </DialogContent>
                            </Dialog>
                            <Dialog open={openUnlinkDialog} fullWidth={false} maxWidth={'lg'} onClose={() => setOpenUnlinkDialog(false)}>
                                <DialogTitle>
                                    <Ico_Warning color='error' sx={{ height: '40px', width: '40px' }} />
                                    <br />
                                    {<>Unlink the Product Master</>}
                                </DialogTitle>
                                <DialogContent>
                                    {
                                        <>
                                            Are you sure you want to unlink the product master?
                                        </>
                                    }
                                    <DialogActions sx={{ mt: '24px' }}>
                                        <Button fullWidth={true} variant='contained' onClick={() => handleUnlink(workItem?.id ?? 0, bookInformation?.BookId)}>Unlink</Button>
                                        <Button fullWidth variant="outlined" onClick={() => { setOpenUnlinkDialog(false) }}>
                                            Cancel
                                        </Button>
                                    </DialogActions>
                                </DialogContent>
                            </Dialog>
                        </Grid>
                        {!isLinked &&
                            <Box sx={{ height: '40px', display: 'flex', mt: '15px', mb: '15px', justifyContent: 'end' }}>
                                <Button variant='outlined' onClick={() => clearSearchResults()}>Cancel</Button>
                                <Button disabled={!permissions?.canLinkProductMaster} variant='contained' onClick={() => setOpenLinkDialog(true)}>Link Product</Button>
                            </Box>
                        }
                    </>
                }
            </Box>)
    }
    else {
        return (
            <>{
                draft &&
                <FormTitle>
                    Product Master
                </FormTitle>
            }
                <Typography variant="h6" align="center" style={{ fontWeight: "bold" }}>No product master available for this Work Item</Typography>
            </>
        )
    }
};

export default WorkItemProductMaster;